import React, {useEffect, useLayoutEffect, useMemo, useState} from "react";
import Dialog from "../../../../../../components/base/dialog";
import {Row} from "reactstrap";
import {CircularProgress, DialogContent, DialogTitle, Divider} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import {OutlinedInput} from "../../../../../../components/base/input/outlined_input";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {formatMoney} from "../../../../../../../core/services/utils/utils";
import Api from "../../../../../../../core/services/api/api";
import {
    dataGridCellTextProps,
    DataGridColumnAlignments,
    DataGridColumnTypes,
    useDataGridSavedState
} from "../../../../../../../packages/data-grid";
import {PriceManagementDialogDataGridCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import PartnerDataGrid from "../../../../../../containers/data-grid";
import {Edit} from "@mui/icons-material";
import IconButton from "../../../../../../components/base/button/icon_button";
import useCachedPriceLevelsDictionaryController from "../../../../../../hooks/use-price-level-dictionary";

const dataGridSaveConfig = {
    name: 'LookupProductInformationPriceManagementDialogDataGrid',
    version: 1,
}

const PartPricesModal = ({open, setOpen, partId,/*partNo*/ refreshAvailabilities}) => {
    const isMounted = useIsMounted();
    const [updating, setUpdating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [priceLevels, setPriceLevels] = useState([]);
    const [updated, setUpdated] = useState(false);
    const {getPriceLevelDictionary} = useCachedPriceLevelsDictionaryController();

    /**
     * Watches for when the modal gets opened. If so, then calls the api for getting the price levels and their
     * minimum prices from server.
     */
    useEffect(() => {
        if (!open) {
            if (updated) {
                refreshAvailabilities();
            }
            setUpdated(false);
            return;
        }
        getProductPriceLevels().then();
    }, [open])

    /**
     * Fetches the list of available price levels of the partner
     */
    const getProductPriceLevels = async () => {
        setLoading(true);
        const [response, priceLevelDict] = await Promise.all([
            Api.getPartLivePriceLevels(partId),
            getPriceLevelDictionary(),
        ]);
        if (!isMounted())
            return;
        if (response?.resultFlag)
            setPriceLevels(response?.data
                ?.sort((a, b) => a.priceLevel.levelIndex - b.priceLevel.levelIndex)
                ?.map(e => ({
                    ...e,
                    priceLevel: {
                        ...e.priceLevel,
                        title: priceLevelDict?.find(d => d.priceLevelId === e.priceLevel?.id)?.title ?? e.priceLevel?.title
                    },
                    isLoading: false,
                })) ?? []);
        setLoading(false);
    }

    /**
     * Updates the price of the price level with the newly provided value
     * @param priceLevel {any} the selected priceLevel
     * @param newValue {any} the selected price
     */
    const updatePriceOfPriceLevel = async (priceLevel, newValue) => {
        setUpdating(true);
        setPriceLevels(prevState => prevState.map(e => e.priceLevel.id === priceLevel.priceLevel.id ? ({
            ...e,
            isLoading: true
        }) : e))
        const forApi = {
            partId: partId,
            priceLevelId: priceLevel.priceLevel.id,
            newPrice: newValue,
        }
        const response = await Api.updatePartPriceLevel(forApi, true);
        if (!isMounted()) return;
        if (response?.resultFlag) {
            setPriceLevels(prevState => prevState.map(e => e.priceLevel?.id === priceLevel.priceLevel.id
                ? {...e, price: newValue}
                : e
            ))
            setUpdated(true);
        }
        setUpdating(false);
        setPriceLevels(prevState => prevState.map(e => e.priceLevel.id === priceLevel.priceLevel.id ? ({
            ...e,
            isLoading: false
        }) : e))
    }

    /**
     * Commits the edited value of the provided cell belonging to the [rowKey] and [colName].
     * @param prevValue
     * @param newValue
     * @param rowKey
     * @param colName
     * @return {Promise<void>}
     */
    const onPriceEdited = async (prevValue, newValue, rowKey, colName) => {
        const _newValue = parseFloat(newValue.toFixed(2));
        let editedPriceLevel = priceLevels.find(e => e.priceLevel.id === rowKey);
        return updatePriceOfPriceLevel(editedPriceLevel, _newValue);
    }

    /**
     * Memo version of data grid columns
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                title: "Price Level",
                name: PriceManagementDialogDataGridCellKeys.priceLevel,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                pinnedToggleable: false,
                width: 150,
            },
            {
                title: "Minimum Price",
                name: PriceManagementDialogDataGridCellKeys.minPrice,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
                sortable: false,
                pinnedToggleable: false,
                width: 100,
            },
            {
                title: "Current Price",
                name: PriceManagementDialogDataGridCellKeys.currentPrice,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                sortable: false,
                pinnedToggleable: false,
                width: 200,
            },
        ], [])

    const [dataGridColumns] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Memo version of data grid columns
     * @type {{cells: {}, data: *, key: *}[]}
     */
    const rows = useMemo(() => {
        return priceLevels?.map((priceLevel) => ({
            key: priceLevel.priceLevel.id,
            data: priceLevel,
            cells: {
                [PriceManagementDialogDataGridCellKeys.priceLevel]: priceLevel?.priceLevel?.title,
                [PriceManagementDialogDataGridCellKeys.minPrice]: priceLevel?.minimumPrice,
                [PriceManagementDialogDataGridCellKeys.currentPrice]: {
                    value: ({openEditPopover}) => <>
                        <IconButton
                            onPressed={openEditPopover}
                            disabled={priceLevel.isLoading}
                            color={'secondary'}
                        >
                            {
                                priceLevel.isLoading
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <Edit className={"icon small"}/>
                            }
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'ml-2'}>
                            {formatMoney(priceLevel?.price ?? 0.0)}
                        </p>
                    </>,
                    editable: true,
                    editableValue: priceLevel.price,
                    EditableContentComponent: <PriceEditor minPrice={priceLevel?.minimumPrice}/>

                }
            }
        }))
    }, [priceLevels])


    return (
        <Dialog
            open={open}
            setOpen={setOpen}
            maxWidth={'md'}
            className={'bound-dialog'}
        >
            <DialogTitle id={'scroll-dialog-title'}>
                <p className={`text-lg text-color-1 my-4 font-weight-semi-bold text-center`}>
                    Price Management
                </p>
                <Divider className='divider'/>
            </DialogTitle>
            <DialogContent>
                <Row className='no-gutters align-items-center w-100 px-2'>
                    <Row className='no-gutters justify-content-center align-items-center w-100'>
                        <PartnerDataGrid
                            {...dataGridSaveConfig}
                            initialLayoutState={{
                                columns: columns,
                            }}
                            columns={dataGridColumns}
                            rows={rows}
                            hideFooter
                            hideToolbar
                            visibleRows={6}
                            state={{
                                loading: {state: loading},
                            }}
                            onCellEdited={onPriceEdited}
                        />
                        <div className='my-1 w-100'/>
                        <Divider className={'divider'}/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open || updating}
                                variant={'dialog'}
                                color={'primary'}
                                onClick={() => setOpen(false)}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                        </Row>
                    </Row>
                </Row>
            </DialogContent>
        </Dialog>
    );
}

const PriceEditor = ({value, setValue, setCanSubmitChanges, minPrice}) => {
    const [showErrorMessage, setShowErrorMessage] = useState(false);

    /**
     * With each change in the value of the editor:
     * - Determines whether the new value can be submitted
     *   which is possible when the input value is valid number and greater than minimum price
     */
    useLayoutEffect(() => {
        const parsed = parseFloat(value)
        setCanSubmitChanges(!Number.isNaN(parsed) && parsed > minPrice);
    }, [value])

    /**
     * Changes the value of this editor.
     *
     * - Checks if entered value is greater than minimum price
     * @param value
     */
    const onValueChanged = (value) => {
        const parsed = parseFloat(value)
        if (!value || Number.isNaN(parsed))
            return setValue(undefined);
        if (parsed < minPrice)
            setShowErrorMessage(true);
        if (parsed > minPrice)
            setShowErrorMessage(false);
        setValue(Math.max(0, parsed))
    }

    return (
        <>
            <OutlinedInput
                fullWidth
                size={'small'}
                label={"New Price"}
                helperText={showErrorMessage ? 'Price should be greater than minimum price' : ''}
                value={value?.toString() ?? ''}
                onChange={onValueChanged}
                type={'number'}
                inputProps={{
                    min: 0,
                }}
            />
        </>
    );
}

export default PartPricesModal;
